// Zero client glue: cache URL + the JWT fetcher the ZeroProvider uses for auth.

import { api } from './api';

export const zeroCacheURL = import.meta.env.VITE_ZERO_CACHE_URL as
    | string
    | undefined;

/**
 * Mint a short-lived Zero JWT from the better-auth session (cookie in the
 * browser, bearer on desktop). Returns null if the user isn't logged in or the
 * request fails. zero-cache verifies the token with the shared
 * ZERO_AUTH_SECRET; its `sub` claim drives permissions.
 *
 * A null here disables host sync entirely, so it is logged rather than
 * swallowed — an empty server list with a silent console was previously
 * indistinguishable from "you have no hosts". The caller retries.
 */
export async function fetchZeroToken(): Promise<string | null> {
    try {
        const { data } = await api.get<{ token: string }>('/api/zero/token');
        return data.token;
    } catch (err) {
        console.warn('[zero] token fetch failed — host sync is offline:', err);
        return null;
    }
}

/**
 * When to refresh, derived from the token's own `exp` (5 min of headroom)
 * rather than a fixed interval: the JWT lasts an hour, and a hardcoded timer
 * in a renderer the OS is free to throttle can drift straight past expiry.
 * Falls back to 50 minutes if the token can't be parsed.
 */
export function zeroTokenRefreshDelay(token: string): number {
    try {
        const payload = token.split('.')[1];
        if (payload) {
            const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
            const { exp } = JSON.parse(json) as { exp?: number };
            if (exp) {
                return Math.max(60_000, exp * 1000 - Date.now() - 5 * 60_000);
            }
        }
    } catch {
        /* fall through to the default */
    }
    return 50 * 60 * 1000;
}

export { useQuery, useZero } from '@rocicorp/zero/react';
